// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getThumbnailUrl } from "@west/XDL/Utilities";
import React, { useState } from "react";

interface OwnProps {
    image: ImageModel;
    defaultImage?: string;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryVideoStyles;
}

type Props = OwnProps;

export interface ProductPrimaryVideoStyles {
    centeringWrapper?: InjectableCss;
    img?: ImgProps;
    playIcon?: IconPresentationProps;
    darkOverlay?: InjectableCss;
}

export const productPrimaryVideoStyles: ProductPrimaryVideoStyles = {};

const ProductPrimaryVideo = ({ image, defaultImage, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryVideoStyles, extendedStyles));

    const videoWrapperClickHandler = () => {
        onClick?.();
    };

    const videoWrapperKeyHandler: React.KeyboardEventHandler<HTMLElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    if (!image.mediumImagePath || image.imageType?.toLowerCase() !== "video") {
        return null;
    }

    const thumbnail = getThumbnailUrl(image.mediumImagePath, defaultImage);
    return (
        <StyledWrapper
            {...styles.centeringWrapper}
            onClick={videoWrapperClickHandler}
            data-test-selector="ProductPrimaryVideo"
        >
            <StyledWrapper {...styles.darkOverlay}></StyledWrapper>
            <Img src={thumbnail} {...styles.img} onKeyPress={videoWrapperKeyHandler} />
            <Icon {...styles.playIcon} />
        </StyledWrapper>
    );
};

export default ProductPrimaryVideo;
